import { Injectable } from '@angular/core';
import { LimegearUserModel } from '../classes/limegearUser.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class CurrentUserStateService {
    private _accessToken: string;
    private _currentUser: LimegearUserModel;

    public accessTokenExpiring = new BehaviorSubject(false);
    public accessRevoked = new BehaviorSubject(false);
    public accessAssigned = new BehaviorSubject(false);

    public timeoutIdle: number;

    public userInactive: Subject<any> = new Subject();
    constructor() {}

    // Initialize user session; call this method on login
    public initCurrentUser(accessToken: string) {
        this._accessToken = accessToken;
        this.setCurrentUser();
        this.startAccessTokenRefreshTimer();
        this.accessAssigned.next(true);
    }

    // Remove user session
    public logout() {
        this._accessToken = null;
        this._currentUser = null;

        this.accessRevoked.next(true);
    }

    public isLoggedIn(): boolean {
        return this.accessToken ? true : false;
    }

    public isLBASuperUser(): boolean {
        return this.isLoggedIn() && this._currentUser?.isSuperUser;
    }

    public isPlatformUser(): boolean {
        return this.isLoggedIn() && this._currentUser?.isPlatformUser;
    }

    public isDemoUser(): boolean {
        return this.isLoggedIn() && this._currentUser?.isDemoUser;
    }

    public isPowerUser(): boolean {
        return this.isLoggedIn() && this._currentUser?.isClientPowerUser;
    }

    public isClientOwner(): boolean {
        return this.isPowerUser() && this._currentUser?.isClientOwner;
    }

    public isBaseClient(): boolean {
        return this.isLoggedIn() && this._currentUser?.baseClient;
    }

    get currentUser(): LimegearUserModel {
        return this.isLoggedIn() ? this._currentUser : null;
    }

    get accessToken(): string {
        return this._accessToken;
    }

    private setCurrentUser(): void {
        try {
            if (this.isLoggedIn()) {
                // Decode JWT from memory
                const jwtHelper = new JwtHelperService();
                const decoded = jwtHelper.decodeToken(this.accessToken);

                // Set currentUser values in memory
                this._currentUser = new LimegearUserModel();
                this._currentUser.clientShortName =
                    decoded['http://limegear.com/claims/ClientShortName'];
                this._currentUser.clientFullName =
                    decoded['http://limegear.com/claims/ClientFullName'];
                this._currentUser.clientCustomRoleName =
                    decoded['http://limegear.com/claims/RoleName'];
                this._currentUser.clientCustomroleAccessName =
                    decoded['http://limegear.com/claims/RoleAccessName'];
                this._currentUser.timeZoneGMTVariance =
                    decoded['http://limegear.com/claims/TimeZoneGMTVariance'];
                this._currentUser.userGUID = decoded['http://limegear.com/claims/UserGUID'];
                this._currentUser.firstAndLastName =
                    decoded['http://limegear.com/claims/FirstAndLastName'];
                this._currentUser.isDSTObserved = decoded['http://limegear.com/claims/IsD'];
                this._currentUser.username = decoded['http://limegear.com/claims/LoginEmail'];
                this._currentUser.accountID = decoded['http://limegear.com/claims/AccountID'];
                let userAcessLevel = decoded['http://limegear.com/claims/UserAccessLevel'];
                if (parseInt(userAcessLevel)) {
                    this._currentUser.userAccessLevel = parseInt(userAcessLevel);
                } else {
                    this._currentUser.userAccessLevel = 0;
                }
                let superUserStatus: any = decoded['http://limegear.com/claims/IsSuperUser'];
                if (superUserStatus === true || superUserStatus.toLowerCase() === 'true') {
                    this._currentUser.isSuperUser = true;
                } else {
                    this._currentUser.isSuperUser = false;
                }

                let platformUserStatus: any = decoded['http://limegear.com/claims/IsPlatformUser'];
                if (platformUserStatus === true || platformUserStatus.toLowerCase() === 'true') {
                    this._currentUser.isPlatformUser = true;
                } else {
                    this._currentUser.isPlatformUser = false;
                }

                let demoUserStatus: any = decoded['http://limegear.com/claims/IsDemoUser'];
                if (demoUserStatus === true || demoUserStatus.toLowerCase() === 'true') {
                    this._currentUser.isDemoUser = true;
                } else {
                    this._currentUser.isDemoUser = false;
                }

                let baseClient: any = decoded['http://limegear.com/claims/BaseClient'];
                if (baseClient === true || baseClient.toLowerCase() === 'true') {
                    this._currentUser.baseClient = true;
                } else {
                    this._currentUser.baseClient = false;
                }

                let clientPowerUserStatus: any =
                    decoded['http://limegear.com/claims/IsClientPowerUser'];
                if (
                    clientPowerUserStatus === true ||
                    clientPowerUserStatus.toLowerCase() === 'true'
                ) {
                    this._currentUser.isClientPowerUser = true;
                } else {
                    this._currentUser.isClientPowerUser = false;
                }

                let clientOwnerUserStatus: any =
                    decoded['http://limegear.com/claims/IsClientOwner'];
                if (
                    clientOwnerUserStatus === true ||
                    clientOwnerUserStatus.toLowerCase() === 'true'
                ) {
                    this._currentUser.isClientOwner = true;
                } else {
                    this._currentUser.isClientOwner = false;
                }

                let hasGLAccess: any = decoded['http://limegear.com/claims/IsClientOwner'];
                if (hasGLAccess === true || hasGLAccess.toLowerCase() === 'true') {
                    this._currentUser.hasGLAccess = true;
                } else {
                    this._currentUser.hasGLAccess = false;
                }

                //console.log('DEBUG: ', this._currentUser);
            } else {
                throw new Error('User is not logged in.');
            }
        } catch (e) {
            throw new Error(e);
        }
    }

    private startAccessTokenRefreshTimer() {
        // parse json object from base64 encoded jwt token
        const jwtToken = JSON.parse(atob(this._accessToken.split('.')[1]));

        // set a timeout to refresh the token a minute before it expires
        const expires = new Date(jwtToken.exp * 1000);
        const timeout = expires.getTime() - Date.now() - 60 * 1000;

        let refreshTokenTimeout = setTimeout(() => {
            this.accessTokenExpiring.next(true);
        }, timeout);
    }
}
